import { lazy } from "react";
import { useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation
} from "react-router-dom-v5-compat";

import loaderWithRetry from "seneca-common/utils/loaderWithRetry";

import useAppVariant from "features/common/hooks/useAppVariant";
import { isSchoolAnalyticsEnabled } from "features/regions/selectors";
import { useShowLoginAndPasswordPage } from "features/settings";
import { CreateAssignmentClassRoutingWrapper } from "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/class/CreateAssignmentClassRoutingWrapper";
import { ROUTES } from "routing/routes";

const LoginAndPasswordPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/settings/features/login-and-password/components/LoginAndPasswordPage"
      )
  )
);
const ClassPage = lazy(() =>
  loaderWithRetry(
    () => import("features/teachers/pages/class-page/components/ClassPage")
  )
);
const ClassPageNavbarLayout = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/class-page/components/ClassPageNavbarLayout"
      )
  )
);
const MultiClassKnowledgeGapsSelectSectionsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/knowledge-gaps/components/steps/KnowledgeGapsSelectSectionsPage/MultiClassKnowledgeGapsSelectSectionsPage"
      )
  )
);
const MultiClassKnowledgeGapsSelectSectionsPageWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/knowledge-gaps/components/steps/KnowledgeGapsSelectSectionsPage/MultiClassKnowledgeGapsSelectSectionsPageWrapper"
      )
  )
);
const SingleClassKnowledgeGapsSelectSectionsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/knowledge-gaps/components/steps/KnowledgeGapsSelectSectionsPage/SingleClassKnowledgeGapsSelectSectionsPage"
      )
  )
);
const SingleClassKnowledgeGapsSelectSectionsPageWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/knowledge-gaps/components/steps/KnowledgeGapsSelectSectionsPage/SingleClassKnowledgeGapsSelectSectionsPageWrapper"
      )
  )
);
const CreateAssignmentClassPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/class/CreateAssignmentClassPage"
      )
  ).then(module => ({ default: module.CreateAssignmentClassPage }))
);
const CreateAssignmentSelectQuizDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/content/CreateAssignmentSelectQuizDataWrapper"
      )
  ).then(module => ({ default: module.CreateAssignmentSelectQuizDataWrapper }))
);
const CreateAssignmentSelectQuizPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/content/CreateAssignmentSelectQuizPage"
      )
  ).then(module => ({ default: module.CreateAssignmentSelectQuizPage }))
);
const CreateAssignmentSelectQuizRoutingWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/content/CreateAssignmentSelectQuizRoutingWrapper"
      )
  ).then(module => ({
    default: module.CreateAssignmentSelectQuizRoutingWrapper
  }))
);
const CreateAssignmentDetailsDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/details/CreateAssignmentDetailsDataWrapper"
      )
  ).then(module => ({ default: module.CreateAssignmentDetailsDataWrapper }))
);
const CreateAssignmentDetailsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/details/CreateAssignmentDetailsPage"
      )
  ).then(module => ({ default: module.CreateAssignmentDetailsPage }))
);
const CreateAssignmentDetailsRoutingWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/details/CreateAssignmentDetailsRoutingWrapper"
      )
  ).then(module => ({ default: module.CreateAssignmentDetailsRoutingWrapper }))
);
const CreateAssignmentExamQuestionsDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/exam-questions/CreateAssignmentExamQuestionsDataWrapper"
      )
  ).then(module => ({
    default: module.CreateAssignmentExamQuestionsDataWrapper
  }))
);
const CreateAssignmentExamQuestionsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/exam-questions/CreateAssignmentExamQuestionsPage"
      )
  ).then(module => ({ default: module.CreateAssignmentExamQuestionsPage }))
);
const CreateAssignmentInviteParentsDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/invite-parents/CreateAssignmentInviteParentsDataWrapper"
      )
  ).then(module => ({
    default: module.CreateAssignmentInviteParentsDataWrapper
  }))
);
const CreateAssignmentInviteParentsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/invite-parents/CreateAssignmentInviteParentsPage"
      )
  ).then(module => ({ default: module.CreateAssignmentInviteParentsPage }))
);
const CreateAssignmentTopicsDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/topics/CreateAssignmentTopicsDataWrapper"
      )
  ).then(module => ({ default: module.CreateAssignmentTopicsDataWrapper }))
);
const CreateAssignmentTopicsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/topics/CreateAssignmentTopicsPage"
      )
  ).then(module => ({ default: module.CreateAssignmentTopicsPage }))
);
const CreateAssignmentTopicsRoutingWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/topics/CreateAssignmentTopicsRoutingWrapper"
      )
  ).then(module => ({ default: module.CreateAssignmentTopicsRoutingWrapper }))
);
const CreateAssignmentTypeDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/type/CreateAssignmentTypeDataWrapper"
      )
  ).then(module => ({ default: module.CreateAssignmentTypeDataWrapper }))
);
const CreateAssignmentTypePage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/type/CreateAssignmentTypePage"
      )
  ).then(module => ({ default: module.CreateAssignmentTypePage }))
);
const CreateAssignmentTypeRoutingWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/type/CreateAssignmentTypeRoutingWrapper"
      )
  ).then(module => ({ default: module.CreateAssignmentTypeRoutingWrapper }))
);
const CreateAssignmentSelectWordsDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/vocabulary/CreateAssignmentSelectWordsDataWrapper"
      )
  ).then(module => ({ default: module.CreateAssignmentSelectWordsDataWrapper }))
);
const CreateAssignmentSelectWordsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/vocabulary/CreateAssignmentSelectWordsPage"
      )
  ).then(module => ({ default: module.CreateAssignmentSelectWordsPage }))
);
const CreateAssignmentSelectWordsRoutingWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/vocabulary/CreateAssignmentSelectWordsRoutingWrapper"
      )
  ).then(module => ({
    default: module.CreateAssignmentSelectWordsRoutingWrapper
  }))
);
const EditAssignmentDetailsDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/EditAssignmentJourney/components/details/EditAssignmentDetailsDataWrapper"
      )
  ).then(module => ({ default: module.EditAssignmentDetailsDataWrapper }))
);
const EditAssignmentDetailsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/EditAssignmentJourney/components/details/EditAssignmentDetailsPage"
      )
  ).then(module => ({ default: module.EditAssignmentDetailsPage }))
);
const EditAssignmentExamQuestionsDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/EditAssignmentJourney/components/exam-questions/EditAssignmentExamQuestionsDataWrapper"
      )
  ).then(module => ({ default: module.EditAssignmentExamQuestionsDataWrapper }))
);
const EditAssignmentExamQuestionsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/EditAssignmentJourney/components/exam-questions/EditAssignmentExamQuestionsPage"
      )
  ).then(module => ({ default: module.EditAssignmentExamQuestionsPage }))
);
const EditAssignmentTopicsDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/EditAssignmentJourney/components/topics/EditAssignmentTopicsDataWrapper"
      )
  ).then(module => ({ default: module.EditAssignmentTopicsDataWrapper }))
);
const EditAssignmentTopicsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/EditAssignmentJourney/components/topics/EditAssignmentTopicsPage"
      )
  ).then(module => ({ default: module.EditAssignmentTopicsPage }))
);
const EditAssignmentWordsDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/EditAssignmentJourney/components/vocabulary/EditAssignmentWordsDataWrapper"
      )
  ).then(module => ({ default: module.EditAssignmentWordsDataWrapper }))
);
const EditAssignmentWordsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/EditAssignmentJourney/components/vocabulary/EditAssignmentWordsPage"
      )
  ).then(module => ({ default: module.EditAssignmentWordsPage }))
);
const CreateSmartAssignmentDuePage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/smart-assignments/journey/CreateSmartAssignmentJourney/components/CreateSmartAssignmentDuePage"
      )
  ).then(module => ({ default: module.CreateSmartAssignmentDuePage }))
);
const CreateSmartAssignmentSectionPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/smart-assignments/journey/CreateSmartAssignmentJourney/components/CreateSmartAssignmentSectionPage"
      )
  ).then(module => ({ default: module.CreateSmartAssignmentSectionPage }))
);
const ClassInsightsCoursePage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/grades/components/insights/course/ClassInsightsCoursePage/ClassInsightsCoursePage"
      )
  ).then(module => ({ default: module.ClassInsightsCoursePage }))
);
const ClassInsightsTopicPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/grades/components/insights/course/ClassInsightsTopicPage/ClassInsightsTopicPage"
      )
  ).then(module => ({ default: module.ClassInsightsTopicPage }))
);
const CreateQuizJourney = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/CreateQuizJourney/components/CreateQuizJourney"
      )
  ).then(module => ({ default: module.CreateQuizJourney }))
);
const GenerateQuiz = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/CreateQuizJourney/components/GenerateQuiz/GenerateQuiz"
      )
  ).then(module => ({ default: module.GenerateQuiz }))
);
const InputResources = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/CreateQuizJourney/components/InputResources/InputResources"
      )
  ).then(module => ({ default: module.InputResources }))
);
const SetDetails = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/CreateQuizJourney/components/SetDetails/SetDetails"
      )
  ).then(module => ({ default: module.SetDetails }))
);
const QuizzesPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/QuizzesPage/QuizzesPage"
      )
  ).then(module => ({ default: module.QuizzesPage }))
);
const SchoolClasses = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/school/components/SchoolClasses/SchoolClasses"
      )
  ).then(module => ({ default: module.SchoolClasses }))
);
const SchoolStudents = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/school/components/SchoolStudents/SchoolStudents"
      )
  ).then(module => ({ default: module.SchoolStudents }))
);
const SchoolTeachers = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/school/components/SchoolTeachers/SchoolTeachers"
      )
  ).then(module => ({ default: module.SchoolTeachers }))
);
const SchoolPage = lazy(() =>
  loaderWithRetry(
    () => import("features/teachers/pages/school/SchoolPage")
  ).then(module => ({ default: module.SchoolPage }))
);
const TeacherPersonalInformationPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/settings/components/tabs/TeacherPersonalInformationPage/TeacherPersonalInformationPage"
      )
  ).then(module => ({ default: module.TeacherPersonalInformationPage }))
);
const TeacherWhatIsPremiumPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/what-is-premium")).then(
    module => ({ default: module.TeacherWhatIsPremiumPage })
  )
);

const EditAssignmentPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "../../features/assignment-journey/components/EditAssignmentJourney/components/EditAssignmentJourneyPage"
      )
  ).then(module => ({ default: module.EditAssignmentPage }))
);
const MultiClassAssignmentPage = lazy(() =>
  loaderWithRetry(
    () => import("../../features/assignment/common/MultiClassAssignmentPage")
  ).then(module => ({ default: module.MultiClassAssignmentPage }))
);

const EditAssignmentsInviteParentsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/EditAssignmentJourney/components/EditAssignmentInviteParentsPage"
      )
  )
);
const EditMultiClassAssignmentDates = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment/components/EditMultiClassAssignmentDates"
      )
  )
);
const MultiClassAssignmentClasses = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment/components/MultiClassAssignmentClasses"
      )
  )
);
const MultiClassAssignmentStudents = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment/components/MultiClassAssignmentStudents"
      )
  )
);
const AssignmentPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/assignment"))
);
const SpeedyMarkingSessionQuitModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/assignment/modals/SpeedyMarkingSessionQuitModal"
      )
  )
);
const SpeedyMarkingSuccessModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/assignment/modals/SpeedyMarkingSuccessModal/SpeedyMarkingSuccessModal"
      )
  )
);
const AssignmentsOverviewPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/assignments-overview"))
);
const BecomeATeacherPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/become-a-teacher"))
);
const OverviewPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/class-overview"))
);
const SettingsPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/class-settings"))
);
const ClassesPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/classes-page"))
);
const CoursesPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/courses"))
);
const GradesPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/grades"))
);
const GradebookPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/grades/components/gradebook/components/GradebookPage/GradebookPage"
      )
  )
);
const HomePage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/homepage"))
);
const InviteStudentsToClassPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/invite-students"))
);
const KnowledgeGapsDetailsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/knowledge-gaps/components/steps/KnowledgeGapsDetailsPage"
      )
  )
);
const CreateKnowledgeGapsJourney = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/knowledge-gaps/containers/CreateKnowledgeGapsJourney"
      )
  )
);
const FilePicker = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/CreateQuizJourney/components/InputResources/components/FilePicker/FilePicker"
      )
  )
);
const ResourceTextArea = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/CreateQuizJourney/components/InputResources/components/ResourceTextArea"
      )
  )
);
const YoutubeLink = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/CreateQuizJourney/components/InputResources/components/YoutubeLink"
      )
  )
);
const QuizPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/QuizPage/components/QuizPage/QuizPage"
      )
  )
);
const QuizzesContextProvider = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/QuizzesPage/QuizzesContext"
      )
  )
);
const SchoolQuizzesPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/quizzes/components/QuizzesPage/SchoolQuizzesPage"
      )
  )
);
const ReportsPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/reports/ReportsPage"))
);
const ResourcePreviewLayout = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/resource-hub/components/ResourcePreviewLayout"
      )
  )
);
const ResourcesPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/resources"))
);
const SchoolGetAQuoteForm = lazy(() =>
  loaderWithRetry(
    () => import("features/teachers/pages/school-get-a-quote-form")
  )
);
const SuccessfulSchoolQuoteForm = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/school-get-a-quote-form/SuccessfulSchoolQuoteForm"
      )
  )
);
const TeacherSettingsPage = lazy(() =>
  loaderWithRetry(() => import("features/teachers/pages/settings"))
);
const TeacherNotificationsPreferencesPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/settings/components/tabs/TeacherNotificationsPreferencesPage/TeacherNotificationsPreferencesPage"
      )
  )
);
const StudentReportCardSyncedSchoolGatingAcrossClasses = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card-across-classes/pages/gating/StudentReportCardSyncedSchoolGating"
      )
  )
);
const StudentReportCardClassSelection = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card-across-classes/pages/StudentReportCardClassSelection/StudentReportCardClassSelection"
      )
  )
);
const StudentReportCardSendingBatchAcrossClasses = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card-across-classes/pages/StudentReportCardPreview/send/batch/StudentReportCardSending"
      )
  )
);
const StudentReportCardSuccessBatchAcrossClasses = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card-across-classes/pages/StudentReportCardPreview/send/batch/StudentReportCardSuccess"
      )
  )
);
const StudentReportCardPageConfirmBatchSendAcrossClasses = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card-across-classes/pages/StudentReportCardPreview/send/confirm/StudentReportCardPageConfirmBatchSend"
      )
  )
);
const StudentReportCardSendingSingleAcrossClasses = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card-across-classes/pages/StudentReportCardPreview/send/single/StudentReportCardSending"
      )
  )
);
const StudentReportCardSuccessSingleAcrossClasses = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card-across-classes/pages/StudentReportCardPreview/send/single/StudentReportCardSuccess"
      )
  )
);
const StudentReportCardsPreview = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card-across-classes/pages/StudentReportCardPreview/StudentReportCardsPreview"
      )
  )
);
const StudentReportCardAcrossClassesPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card-across-classes/StudentReportCardAcrossClassesPage"
      )
  )
);
const StudentReportCardSyncedSchoolGating = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card/page/gating/StudentReportCardSyncedSchoolGating"
      )
  )
);
const StudentReportCardSendingBatch = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card/page/send/batch/StudentReportCardSending"
      )
  )
);
const StudentReportCardSuccessBatch = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card/page/send/batch/StudentReportCardSuccess"
      )
  )
);
const StudentReportCardPageConfirmBatchSend = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card/page/send/confirm/StudentReportCardPageConfirmBatchSend"
      )
  )
);
const StudentReportCardSendingSingle = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card/page/send/single/StudentReportCardSending"
      )
  )
);
const StudentReportCardSuccessSingle = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card/page/send/single/StudentReportCardSuccess"
      )
  )
);
const StudentReportCardPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card/page/StudentReportCardPage"
      )
  )
);
const TeacherAssignmentsPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/teacher-assignments/components/TeacherAssignmentsPage/TeacherAssignmentsPage"
      )
  )
);
const AddResourcePage = lazy(() =>
  loaderWithRetry(
    () => import("../../../resource-discover/pages/AddResourcePage")
  )
);
const ResourceDiscoverPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "../../../resource-discover/pages/ResourceDiscoverPage/ResourceDiscoverPage"
      )
  )
);
const ResourcePreviewPage = lazy(() =>
  loaderWithRetry(
    () => import("../../../resource-discover/pages/ResourcePreviewPage")
  )
);
const StudentInsightsPage = lazy(() =>
  loaderWithRetry(() => import("../../pages/student-insights"))
);
const ReassignRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "./routes/class/routes/assignments/routes/assignment/routes/reassign/Route"
      )
  )
);
const EditAssignmentRoute = lazy(() =>
  loaderWithRetry(() => import("./routes/class/routes/edit-assignment/Route"))
);
const StudentsRoute = lazy(() =>
  loaderWithRetry(() => import("./routes/class/routes/students/Route"))
);

const AddCoursesPage = lazy(() =>
  loaderWithRetry(
    () => import("features/teachers/components/common/AddCourses")
  )
);
const TeacherDashboard = lazy(() =>
  loaderWithRetry(
    () => import("features/teachers/components/common/TeacherDashboard")
  )
);
const TeacherNavbarLayout = lazy(() =>
  loaderWithRetry(
    () => import("features/teachers/components/layout/TeacherNavbarLayout")
  )
);
const CreateAssignmentJourney = lazy(() =>
  loaderWithRetry(
    () => import("features/teachers/features/assignment-journey")
  ).then(module => ({ default: module.CreateAssignmentJourney }))
);
const CreateAssignmentClassDataWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/features/assignment-journey/components/CreateAssignmentJouney/components/class/CreateAssignmentClassDataWrapper"
      )
  ).then(module => ({ default: module.CreateAssignmentClassDataWrapper }))
);
const StudentsReportPage = lazy(() =>
  loaderWithRetry(
    () => import("features/teachers/pages/reports/pages/StudentsReportPage")
  )
);

const TeachersReportPage = lazy(() =>
  loaderWithRetry(
    () => import("features/teachers/pages/reports/pages/TeachersReportPage")
  )
);

const ParentsReportPage = lazy(() =>
  loaderWithRetry(
    () => import("features/teachers/pages/reports/pages/ParentsReportPage")
  )
);

const SpeedyMarkingPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/assignment/components/SpeedyMarking/SpeedyMarkingSession"
      )
  )
);

const StudentReportCardPreview = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card/page/preview/StudentReportCardPreview"
      )
  )
);

const StudentReportCardPreviewContent = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/student/report-card-across-classes/pages/StudentReportCardPreview/components/StudentReportCardPreviewContent"
      )
  )
);

const ResourceHubPage = lazy(() =>
  loaderWithRetry(
    () =>
      import("features/teachers/pages/resource-hub/components/ResourceHubPage")
  )
);

const YourResourcesPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/resource-hub/components/YourResourcesPage"
      )
  )
);

const SchoolResourcesPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/resource-hub/components/SchoolResourcesPage"
      )
  )
);

const ResourceHubLayout = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/resource-hub/components/ResourceHubLayout"
      )
  )
);

const PreviewSectionPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        "features/teachers/pages/resource-hub/components/PreviewSectionPage"
      )
  )
);

const PreviewQuizPage = lazy(() =>
  loaderWithRetry(
    () =>
      import("features/teachers/pages/resource-hub/components/PreviewQuizPage")
  )
);

const {
  ASSIGNMENTS,
  BECOME_A_TEACHER,
  CLASS,
  CLASSES,
  HOME,
  QUIZZES,
  REPORTS,
  RESOURCES,
  RESOURCE_HUB,
  SCHOOL,
  SETTINGS,
  STUDENT_INSIGHTS,
  WHAT_IS_PREMIUM
} = ROUTES.TEACHER.$;

export default function TeacherRouter() {
  const { state } = useLocation();
  const showLoginAndPasswordPage = useShowLoginAndPasswordPage();
  const schoolAnalyticsEnabled = useSelector(isSchoolAnalyticsEnabled);
  const appVariant = useAppVariant();

  const isGoStudentLearning = appVariant === "goStudentLearning";

  return (
    <Routes>
      {!isGoStudentLearning && (
        <Route
          path={CLASS.ASSIGNMENTS.ASSIGNMENT.SPEEDY_MARKING.relativePath}
          element={<SpeedyMarkingPage />}
        >
          <Route
            path={
              CLASS.ASSIGNMENTS.ASSIGNMENT.SPEEDY_MARKING.$.FINISH.relativePath
            }
            element={<SpeedyMarkingSuccessModal />}
          />
          <Route
            path={
              CLASS.ASSIGNMENTS.ASSIGNMENT.SPEEDY_MARKING.$.QUIT.relativePath
            }
            element={<SpeedyMarkingSessionQuitModal />}
          />
        </Route>
      )}

      <Route element={<ResourcePreviewLayout />}>
        <Route
          path={RESOURCE_HUB.PREVIEW.relativePath}
          element={<TeacherDashboard />}
        >
          <Route
            path={RESOURCE_HUB.PREVIEW.$.SECTION.relativePath}
            element={<PreviewSectionPage />}
          />
          <Route
            path={RESOURCE_HUB.PREVIEW.$.QUIZ.relativePath}
            element={<PreviewQuizPage />}
          />
          <Route
            path="*"
            index
            element={
              <Navigate
                to={ROUTES.TEACHER.RESOURCE_HUB.buildPath({})}
                replace
                state={state}
              />
            }
          />
        </Route>
      </Route>

      <Route element={<TeacherDashboard />}>
        <Route
          path={ASSIGNMENTS.CREATE.relativePath}
          element={
            <QuizzesContextProvider>
              <CreateAssignmentJourney />
            </QuizzesContextProvider>
          }
        >
          <Route
            path={ASSIGNMENTS.CREATE.$.CLASS.relativePath}
            element={
              <CreateAssignmentClassRoutingWrapper>
                <CreateAssignmentClassDataWrapper>
                  <CreateAssignmentClassPage />
                </CreateAssignmentClassDataWrapper>
              </CreateAssignmentClassRoutingWrapper>
            }
          />
          <Route
            path={ASSIGNMENTS.CREATE.$.TYPE.relativePath}
            element={
              <CreateAssignmentTypeRoutingWrapper>
                <CreateAssignmentTypeDataWrapper>
                  <CreateAssignmentTypePage />
                </CreateAssignmentTypeDataWrapper>
              </CreateAssignmentTypeRoutingWrapper>
            }
          />
          <Route
            path={ASSIGNMENTS.CREATE.$.TOPICS.relativePath}
            element={
              <CreateAssignmentTopicsRoutingWrapper>
                <CreateAssignmentTopicsDataWrapper>
                  <CreateAssignmentTopicsPage />
                </CreateAssignmentTopicsDataWrapper>
              </CreateAssignmentTopicsRoutingWrapper>
            }
          />
          <Route
            path={ASSIGNMENTS.CREATE.$.QUIZZES.relativePath}
            element={
              <CreateAssignmentSelectQuizRoutingWrapper>
                <CreateAssignmentSelectQuizDataWrapper>
                  <CreateAssignmentSelectQuizPage />
                </CreateAssignmentSelectQuizDataWrapper>
              </CreateAssignmentSelectQuizRoutingWrapper>
            }
          />
          <Route
            path={ASSIGNMENTS.CREATE.$.VOCABULARY.relativePath}
            element={
              <CreateAssignmentSelectWordsRoutingWrapper>
                <CreateAssignmentSelectWordsDataWrapper>
                  <CreateAssignmentSelectWordsPage />
                </CreateAssignmentSelectWordsDataWrapper>
              </CreateAssignmentSelectWordsRoutingWrapper>
            }
          />
          <Route
            path={ASSIGNMENTS.CREATE.$.EXAM_QUESTIONS.relativePath}
            element={
              <CreateAssignmentExamQuestionsDataWrapper>
                <CreateAssignmentExamQuestionsPage />
              </CreateAssignmentExamQuestionsDataWrapper>
            }
          />
          <Route
            path={ASSIGNMENTS.CREATE.$.DETAILS.relativePath}
            element={
              <CreateAssignmentDetailsRoutingWrapper>
                <CreateAssignmentDetailsDataWrapper>
                  <CreateAssignmentDetailsPage />
                </CreateAssignmentDetailsDataWrapper>
              </CreateAssignmentDetailsRoutingWrapper>
            }
          />
          <Route
            path={ASSIGNMENTS.CREATE.$.INVITE_PARENTS.relativePath}
            element={
              <CreateAssignmentInviteParentsDataWrapper>
                <CreateAssignmentInviteParentsPage />
              </CreateAssignmentInviteParentsDataWrapper>
            }
          />
          <Route
            path="*"
            index
            element={
              <Navigate
                to={ASSIGNMENTS.CREATE.$.CLASS.buildRelativePath({})}
                replace
                state={state}
              />
            }
          />
        </Route>

        {!isGoStudentLearning && (
          <>
            <Route
              path={CLASS.ASSIGNMENTS.ASSIGNMENT.REASSIGN.relativePath}
              element={<ReassignRoute />}
            >
              <Route
                path={
                  CLASS.ASSIGNMENTS.ASSIGNMENT.REASSIGN.$.SECTION.relativePath
                }
                element={<CreateSmartAssignmentSectionPage />}
              />
              <Route
                path={CLASS.ASSIGNMENTS.ASSIGNMENT.REASSIGN.$.DUE.relativePath}
                element={<CreateSmartAssignmentDuePage />}
              />
              <Route
                path="*"
                index
                element={
                  <Navigate
                    to={CLASS.ASSIGNMENTS.ASSIGNMENT.REASSIGN.$.SECTION.buildRelativePath(
                      {}
                    )}
                    replace
                    state={state}
                  />
                }
              />
            </Route>

            <Route
              path={CLASS.EDIT_ASSIGNMENT.relativePath}
              element={<EditAssignmentRoute />}
            >
              <Route index element={<EditAssignmentPage />} />
              <Route
                path={CLASS.EDIT_ASSIGNMENT.$.TOPICS.relativePath}
                element={
                  <EditAssignmentTopicsDataWrapper>
                    <EditAssignmentTopicsPage />
                  </EditAssignmentTopicsDataWrapper>
                }
              />
              <Route
                path={CLASS.EDIT_ASSIGNMENT.$.DETAILS.relativePath}
                element={
                  <EditAssignmentDetailsDataWrapper>
                    <EditAssignmentDetailsPage />
                  </EditAssignmentDetailsDataWrapper>
                }
              />
              <Route
                path={CLASS.EDIT_ASSIGNMENT.$.EXAM_QUESTIONS.relativePath}
                element={
                  <EditAssignmentExamQuestionsDataWrapper>
                    <EditAssignmentExamQuestionsPage />
                  </EditAssignmentExamQuestionsDataWrapper>
                }
              />
              <Route
                path={CLASS.EDIT_ASSIGNMENT.$.VOCABULARY.relativePath}
                element={
                  <EditAssignmentWordsDataWrapper>
                    <EditAssignmentWordsPage />
                  </EditAssignmentWordsDataWrapper>
                }
              />
              <Route
                path={CLASS.EDIT_ASSIGNMENT.$.INVITE_PARENTS.relativePath}
                element={<EditAssignmentsInviteParentsPage />}
              />
              <Route
                path="*"
                element={
                  <Navigate
                    replace
                    to={CLASS.EDIT_ASSIGNMENT.$.TOPICS.buildRelativePath({})}
                    state={state}
                  />
                }
              />
            </Route>
          </>
        )}
      </Route>

      <Route element={<TeacherNavbarLayout />}>
        <Route element={<TeacherDashboard />}>
          <Route path={ASSIGNMENTS.relativePath}>
            <Route index element={<TeacherAssignmentsPage />} />
            {!isGoStudentLearning && (
              <Route
                path={ASSIGNMENTS.$.ASSIGNMENT.relativePath}
                element={<MultiClassAssignmentPage />}
              >
                <Route
                  path={ASSIGNMENTS.ASSIGNMENT.$.CLASSES.relativePath}
                  element={<MultiClassAssignmentClasses />}
                />
                <Route
                  path={ASSIGNMENTS.ASSIGNMENT.$.STUDENTS.relativePath}
                  element={<MultiClassAssignmentStudents />}
                />
                <Route
                  path={ASSIGNMENTS.ASSIGNMENT.$.EDIT.relativePath}
                  element={<EditMultiClassAssignmentDates />}
                />
                <Route
                  path="*"
                  index
                  element={
                    <Navigate
                      to={ASSIGNMENTS.ASSIGNMENT.$.CLASSES.buildRelativePath(
                        {}
                      )}
                      replace
                      state={state}
                    />
                  }
                />
              </Route>
            )}
            <Route
              path="*"
              element={
                <Navigate
                  to={ROUTES.TEACHER.ASSIGNMENTS.buildPath({})}
                  replace
                  state={state}
                />
              }
            />
          </Route>

          <Route
            path={RESOURCE_HUB.relativePath}
            element={<ResourceHubLayout />}
          >
            <Route index element={<ResourceHubPage />} />
            <Route
              path={RESOURCE_HUB.$.ME.relativePath}
              element={<YourResourcesPage />}
            />
            <Route
              path={RESOURCE_HUB.$.SCHOOL.relativePath}
              element={<SchoolResourcesPage />}
            />
            <Route
              path="*"
              element={
                <Navigate
                  to={ROUTES.TEACHER.RESOURCE_HUB.buildPath({})}
                  replace
                  state={state}
                />
              }
            />
          </Route>

          {!isGoStudentLearning && (
            <>
              <Route
                path={BECOME_A_TEACHER.relativePath}
                element={<BecomeATeacherPage />}
              />

              <Route path={CLASS.relativePath} element={<ClassPage />}>
                <Route
                  path={CLASS.$.INVITE_STUDENTS.relativePath}
                  element={<InviteStudentsToClassPage />}
                />
                <Route
                  path={CLASS.$.ADD_COURSES.relativePath}
                  element={<AddCoursesPage />}
                />
                <Route element={<ClassPageNavbarLayout />}>
                  <Route
                    path={CLASS.$.STUDENTS.relativePath}
                    element={<StudentsRoute />}
                  />
                  <Route
                    path={CLASS.$.GRADES.relativePath}
                    element={<GradesPage />}
                  >
                    <Route
                      path={CLASS.GRADES.$.GRADEBOOK.relativePath}
                      element={<GradebookPage />}
                    />
                    <Route
                      path={CLASS.GRADES.$.INSIGHTS_TOPIC.relativePath}
                      element={<ClassInsightsTopicPage />}
                    />
                    <Route
                      path={CLASS.GRADES.$.INSIGHTS_COURSE.relativePath}
                      element={<ClassInsightsCoursePage />}
                    />
                    <Route
                      path="*"
                      index
                      element={
                        <Navigate
                          to={CLASS.GRADES.$.GRADEBOOK.buildRelativePath({})}
                          replace
                          state={state}
                        />
                      }
                    />
                  </Route>
                  <Route
                    path={CLASS.$.KNOWLEDGE_GAPS.relativePath}
                    element={<CreateKnowledgeGapsJourney />}
                  >
                    <Route
                      path={CLASS.KNOWLEDGE_GAPS.$.SECTIONS.relativePath}
                      element={
                        <SingleClassKnowledgeGapsSelectSectionsPageWrapper>
                          <SingleClassKnowledgeGapsSelectSectionsPage />
                        </SingleClassKnowledgeGapsSelectSectionsPageWrapper>
                      }
                    />
                    <Route
                      path={CLASS.KNOWLEDGE_GAPS.$.DETAILS.relativePath}
                      element={<KnowledgeGapsDetailsPage />}
                    />
                    <Route
                      path="*"
                      index
                      element={
                        <Navigate
                          to={CLASS.KNOWLEDGE_GAPS.$.SECTIONS.buildRelativePath(
                            {}
                          )}
                          replace
                          state={state}
                        />
                      }
                    />
                  </Route>
                  <Route
                    path={CLASS.$.COURSES.relativePath}
                    element={<CoursesPage />}
                  />
                  <Route path={CLASS.$.ASSIGNMENTS.relativePath}>
                    <Route
                      path={CLASS.ASSIGNMENTS.$.OVERVIEW.relativePath}
                      element={<AssignmentsOverviewPage />}
                    />
                    <Route
                      path={CLASS.ASSIGNMENTS.$.ASSIGNMENT.relativePath}
                      element={<AssignmentPage />}
                    />
                    <Route
                      path="*"
                      index
                      element={
                        <Navigate
                          to={CLASS.ASSIGNMENTS.$.OVERVIEW.buildRelativePath(
                            {}
                          )}
                          replace
                          state={state}
                        />
                      }
                    />
                  </Route>
                  <Route
                    path={CLASS.$.SETTINGS.relativePath}
                    element={<SettingsPage />}
                  />
                  <Route
                    path={CLASS.$.OVERVIEW.relativePath}
                    element={<OverviewPage />}
                  />
                  <Route path={CLASS.$.STUDENT.REPORT_CARD.relativePath}>
                    <Route
                      path={
                        CLASS.STUDENT.REPORT_CARD.$.BATCH_SENDING.relativePath
                      }
                      element={<StudentReportCardSendingBatch />}
                    />
                    <Route
                      path={
                        CLASS.STUDENT.REPORT_CARD.$.BATCH_SUCCESS.relativePath
                      }
                      element={<StudentReportCardSuccessBatch />}
                    />
                    <Route
                      path={
                        CLASS.STUDENT.REPORT_CARD.$.CONFIRM_BATCH_SEND
                          .relativePath
                      }
                      element={
                        <StudentReportCardPageConfirmBatchSend featureType="single-class" />
                      }
                    />
                    <Route
                      path={
                        CLASS.STUDENT.REPORT_CARD.$.SYNCED_SCHOOL_GATING
                          .relativePath
                      }
                      element={<StudentReportCardSyncedSchoolGating />}
                    />
                    <Route path="*" element={<StudentReportCardPage />}>
                      <Route
                        path={CLASS.STUDENT.REPORT_CARD.$.PREVIEW.relativePath}
                        element={
                          <StudentReportCardPreview featureType="single-class" />
                        }
                      />
                      <Route
                        path={CLASS.STUDENT.REPORT_CARD.$.SENDING.relativePath}
                        element={<StudentReportCardSendingSingle />}
                      />
                      <Route
                        path={CLASS.STUDENT.REPORT_CARD.$.SUCCESS.relativePath}
                        element={<StudentReportCardSuccessSingle />}
                      />
                      <Route
                        path="*"
                        element={
                          <Navigate
                            replace
                            to={`../../../${CLASS.$.GRADES.GRADEBOOK.buildRelativePath({})}`}
                            state={state}
                          />
                        }
                      />
                    </Route>
                  </Route>
                  <Route
                    path="*"
                    index
                    element={
                      <Navigate
                        to={CLASS.$.OVERVIEW.buildRelativePath({})}
                        replace
                        state={state}
                      />
                    }
                  />
                </Route>
              </Route>

              <Route path={CLASSES.relativePath} element={<ClassesPage />} />

              <Route
                path={RESOURCES.relativePath}
                element={<ResourcesPage />}
              />

              <Route path={SCHOOL.relativePath} element={<SchoolPage />}>
                <Route
                  path={SCHOOL.$.CLASSES.relativePath}
                  element={<SchoolClasses />}
                />
                <Route
                  path={SCHOOL.$.STUDENTS.relativePath}
                  element={<SchoolStudents />}
                />
                <Route
                  path={SCHOOL.$.TEACHERS.relativePath}
                  element={<SchoolTeachers />}
                />
                <Route
                  path="*"
                  index
                  element={
                    <Navigate
                      to={SCHOOL.$.CLASSES.buildRelativePath({})}
                      replace
                      state={state}
                    />
                  }
                />
              </Route>

              <Route path={STUDENT_INSIGHTS.relativePath}>
                <Route
                  path={STUDENT_INSIGHTS.$.KNOWLEDGE_GAPS.relativePath}
                  element={<CreateKnowledgeGapsJourney />}
                >
                  <Route
                    path={
                      STUDENT_INSIGHTS.KNOWLEDGE_GAPS.$.SECTIONS.relativePath
                    }
                    element={
                      <MultiClassKnowledgeGapsSelectSectionsPageWrapper>
                        <MultiClassKnowledgeGapsSelectSectionsPage />
                      </MultiClassKnowledgeGapsSelectSectionsPageWrapper>
                    }
                  />
                  <Route
                    path={
                      STUDENT_INSIGHTS.KNOWLEDGE_GAPS.$.DETAILS.relativePath
                    }
                    element={<KnowledgeGapsDetailsPage />}
                  />
                  <Route
                    path="*"
                    index
                    element={
                      <Navigate
                        to={STUDENT_INSIGHTS.KNOWLEDGE_GAPS.$.SECTIONS.buildRelativePath(
                          {}
                        )}
                        replace
                        state={state}
                      />
                    }
                  />
                </Route>
                <Route
                  path={STUDENT_INSIGHTS.$.OVERVIEW.relativePath}
                  element={<StudentInsightsPage />}
                />
                <Route
                  path={STUDENT_INSIGHTS.$.REPORT_CARD.relativePath}
                  element={<StudentReportCardAcrossClassesPage />}
                >
                  <Route
                    path={STUDENT_INSIGHTS.REPORT_CARD.$.SELECT.relativePath}
                    element={<StudentReportCardClassSelection />}
                  />
                  <Route element={<StudentReportCardsPreview />}>
                    <Route
                      path={STUDENT_INSIGHTS.REPORT_CARD.$.PREVIEW.relativePath}
                      element={<StudentReportCardPreviewContent />}
                    />
                    <Route
                      path={STUDENT_INSIGHTS.REPORT_CARD.$.SENDING.relativePath}
                      element={<StudentReportCardSendingSingleAcrossClasses />}
                    />
                    <Route
                      path={STUDENT_INSIGHTS.REPORT_CARD.$.SUCCESS.relativePath}
                      element={<StudentReportCardSuccessSingleAcrossClasses />}
                    />
                  </Route>

                  <Route
                    path={
                      STUDENT_INSIGHTS.REPORT_CARD.$.CONFIRM_BATCH_SEND
                        .relativePath
                    }
                    element={
                      <StudentReportCardPageConfirmBatchSendAcrossClasses />
                    }
                  />
                  <Route
                    path={
                      STUDENT_INSIGHTS.REPORT_CARD.$.BATCH_SENDING.relativePath
                    }
                    element={<StudentReportCardSendingBatchAcrossClasses />}
                  />
                  <Route
                    path={
                      STUDENT_INSIGHTS.REPORT_CARD.$.BATCH_SUCCESS.relativePath
                    }
                    element={<StudentReportCardSuccessBatchAcrossClasses />}
                  />
                  <Route
                    path={
                      STUDENT_INSIGHTS.REPORT_CARD.$.SYNCED_SCHOOL_GATING
                        .relativePath
                    }
                    element={
                      <StudentReportCardSyncedSchoolGatingAcrossClasses />
                    }
                  />
                  <Route
                    path="*"
                    element={
                      <Navigate to={ROUTES.TEACHER.CLASSES.buildPath({})} />
                    }
                  />
                </Route>
                <Route
                  path="*"
                  index
                  element={
                    <Navigate
                      to={STUDENT_INSIGHTS.$.OVERVIEW.buildRelativePath({})}
                      replace
                      state={state}
                    />
                  }
                />
              </Route>

              <Route path={WHAT_IS_PREMIUM.relativePath}>
                <Route index element={<TeacherWhatIsPremiumPage />} />
                <Route
                  path={WHAT_IS_PREMIUM.$.GET_A_QUOTE.relativePath}
                  element={<SchoolGetAQuoteForm />}
                />
                <Route
                  path={WHAT_IS_PREMIUM.$.GET_A_QUOTE.SUCCESS.relativePath}
                  element={<SuccessfulSchoolQuoteForm />}
                />
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={ROUTES.TEACHER.WHAT_IS_PREMIUM.buildPath({})}
                      replace
                      state={state}
                    />
                  }
                />
              </Route>
            </>
          )}

          <Route
            path={HOME.relativePath}
            element={
              !isGoStudentLearning ? (
                <HomePage />
              ) : (
                <Navigate
                  to={ROUTES.TEACHER.RESOURCE_HUB.buildPath({})}
                  replace
                  state={state}
                />
              )
            }
          />

          <Route path={QUIZZES.relativePath}>
            <Route index element={<QuizzesPage />} />
            <Route path={QUIZZES.$.QUIZ.relativePath} element={<QuizPage />} />
            <Route
              path={QUIZZES.$.SCHOOL.relativePath}
              element={<SchoolQuizzesPage />}
            />
            <Route
              path={QUIZZES.$.CREATE.relativePath}
              element={<CreateQuizJourney />}
            >
              <Route
                path={QUIZZES.CREATE.$.DETAILS.relativePath}
                element={<SetDetails />}
              />
              <Route
                path={QUIZZES.CREATE.$.GENERATING.relativePath}
                element={<GenerateQuiz />}
              />
              <Route
                path={QUIZZES.CREATE.$.INPUT.relativePath}
                element={<InputResources />}
              >
                <Route
                  path={QUIZZES.CREATE.INPUT.$.PASTE_TEXT.relativePath}
                  element={<ResourceTextArea />}
                />
                <Route
                  path={QUIZZES.CREATE.INPUT.$.YOUTUBE_LINK.relativePath}
                  element={<YoutubeLink />}
                />
                <Route
                  path={QUIZZES.CREATE.INPUT.$.UPLOAD_FILE.relativePath}
                  element={<FilePicker />}
                />
                <Route
                  path="*"
                  index
                  element={
                    <Navigate
                      to={QUIZZES.CREATE.INPUT.$.PASTE_TEXT.buildRelativePath(
                        {}
                      )}
                      replace
                      state={state}
                    />
                  }
                />
              </Route>
            </Route>
            <Route
              path="*"
              element={
                <Navigate
                  to={ROUTES.TEACHER.QUIZZES.buildPath({})}
                  replace
                  state={state}
                />
              }
            />
          </Route>

          {schoolAnalyticsEnabled && !isGoStudentLearning && (
            <Route path={REPORTS.relativePath} element={<ReportsPage />}>
              <Route
                path={REPORTS.$.STUDENTS.relativePath}
                element={<StudentsReportPage />}
              />
              <Route
                path={REPORTS.$.TEACHERS.relativePath}
                element={<TeachersReportPage />}
              />
              <Route
                path={REPORTS.$.PARENTS.relativePath}
                element={<ParentsReportPage />}
              />
              <Route
                path="*"
                index
                element={
                  <Navigate
                    to={REPORTS.$.STUDENTS.buildRelativePath({})}
                    replace
                    state={state}
                  />
                }
              />
            </Route>
          )}

          <Route path={SETTINGS.relativePath} element={<TeacherSettingsPage />}>
            <Route
              path={ROUTES.TEACHER.SETTINGS.$.PERSONAL_INFORMATION.relativePath}
              element={<TeacherPersonalInformationPage />}
            />
            {showLoginAndPasswordPage && (
              <Route
                path={ROUTES.TEACHER.SETTINGS.$.PASSWORD.relativePath}
                element={<LoginAndPasswordPage />}
              />
            )}
            {!isGoStudentLearning && (
              <Route
                path={ROUTES.TEACHER.SETTINGS.$.NOTIFICATIONS.relativePath}
                element={<TeacherNotificationsPreferencesPage />}
              />
            )}
            <Route
              path="*"
              index
              element={
                <Navigate
                  to={ROUTES.TEACHER.SETTINGS.$.PERSONAL_INFORMATION.buildRelativePath(
                    {}
                  )}
                  replace
                />
              }
            />
          </Route>

          <Route
            path="*"
            index
            element={
              <Navigate
                to={ROUTES.TEACHER.HOME.buildPath({})}
                replace
                state={state}
              />
            }
          />
        </Route>
      </Route>
    </Routes>
  );
}
